/**
 * The signed-in user and their organizations, loaded once after login.
 *
 * Screens that need the user's organization (the Org Admin page, navigation
 * gating) read it here instead of from configuration: the organization belongs
 * to the user, not to the environment.
 *
 * The organization the session acts in is chosen by the server (`isCurrent`):
 * the access token is tied to one organization and every organization-scoped
 * operation authorizes against it. There is deliberately no selection state
 * here, so the UI can never show an organization the backend is not using.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  getCurrentUser,
  type CurrentUser,
  type Organization,
} from '../../api/currentUser';

/**
 * What a consuming screen should render. `ready` is the only state in which
 * `user` and `currentOrganization` are set. `preview` is the frontend-only
 * preview mode (`ses.projectPreviewMode`): there is no backend to ask, so no
 * user either.
 */
export type CurrentUserStatus = 'loading' | 'error' | 'ready' | 'preview';

/** Narrow on `status`: only `ready` carries a user and a current organization. */
export type CurrentUserContextValue =
  | {
      status: Exclude<CurrentUserStatus, 'ready'>;
      user: null;
      organizations: Organization[];
      currentOrganization: null;
      reload: () => void;
    }
  | {
      status: 'ready';
      user: CurrentUser['user'];
      organizations: Organization[];
      currentOrganization: Organization;
      reload: () => void;
    };

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

type LoadState =
  | { status: 'loading' | 'error' }
  | { status: 'ready'; data: CurrentUser; currentOrganization: Organization };

export const CurrentUserProvider = ({ children }: PropsWithChildren<{}>) => {
  const config = useApi(configApiRef);
  const isPreviewMode =
    config.getOptionalBoolean('ses.projectPreviewMode') ?? false;

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  // Only the latest request may update the state: a slower earlier response, or
  // one that lands after unmount, must not overwrite a newer result.
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setState({ status: 'loading' });
    try {
      const data = await getCurrentUser();
      if (request !== requestRef.current) return;
      // Exactly one organization must be current; never guess one.
      const current = data.organizations.filter(o => o.isCurrent);
      setState(
        current.length === 1
          ? { status: 'ready', data, currentOrganization: current[0] }
          : { status: 'error' },
      );
    } catch {
      if (request === requestRef.current) {
        setState({ status: 'error' });
      }
    }
  }, []);

  useEffect(() => {
    if (isPreviewMode) return undefined;
    void load();
    return () => {
      requestRef.current += 1;
    };
  }, [isPreviewMode, load]);

  const reload = useCallback(() => {
    if (!isPreviewMode) void load();
  }, [isPreviewMode, load]);

  const value = useMemo<CurrentUserContextValue>(() => {
    if (isPreviewMode) {
      return {
        status: 'preview',
        user: null,
        organizations: [],
        currentOrganization: null,
        reload,
      };
    }
    if (state.status === 'ready') {
      return {
        status: 'ready',
        user: state.data.user,
        organizations: state.data.organizations,
        currentOrganization: state.currentOrganization,
        reload,
      };
    }
    return {
      status: state.status,
      user: null,
      organizations: [],
      currentOrganization: null,
      reload,
    };
  }, [isPreviewMode, state, reload]);

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

/**
 * Read the current user. Throws when used outside the provider — that is a
 * wiring mistake, not a runtime condition worth rendering around.
 */
export function useCurrentUser(): CurrentUserContextValue {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }
  return ctx;
}

/**
 * Whether the user administers the organization (org-level files, etc.).
 * Platform staff never do: they hold no organization grants.
 */
export function canManageOrganization(
  organization: Organization | null,
): boolean {
  return organization?.role === 'admin' && !organization.isPlatformStaff;
}

/**
 * Whether the Org Admin entry belongs in the navigation: for organization
 * admins, and in the frontend-only preview mode, where there is no backend to
 * ask who the user is.
 */
export function canSeeOrgAdmin(
  status: CurrentUserStatus,
  currentOrganization: Organization | null,
): boolean {
  return status === 'preview' || canManageOrganization(currentOrganization);
}
